import { Injectable } from '@angular/core';
import {
  ADMIN_ROLE,
  CAREAPP_MANAGER,
  CAREAPP_SUPPORT_1,
  CAREAPP_SUPPORT_2,
} from '@careapp-group/core';
import {
  AccountService,
  MembersService,
  PermissionUpdateDto,
  PermissionsService,
} from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, createAction } from '@ngrx/store';
import { of as observableOf } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CAAction } from '../actions';
import { Members } from '../actions/index';
import { getAuthenticationState } from '../auth/auth.selectors';
import { Memos } from '../memos/memos.actions';
import * as selectors from '../reducers/index';
import { getCurrentOrganisationTenantId } from '../organisations/organisations.selectors';
import { getSelectedCustomerOrganisationId } from '../customers/customers.selectors';

@Injectable()
export class MembersEffects {
  paginate = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.PAGE_CHANGE),
      map((action) => Members.RequestAllAction())
    )
  );

  updateFilter = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.UPDATE_FILTER),
      map((action) => Members.RequestPageAction())
    )
  );

  updatePagination = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CAAction>(Members.RESET_PAGE, Members.REQUEST_PAGE),
        tap((action) => {
          this.store.dispatch(Members.RequestAllAction());
        })
      ),
    { dispatch: false }
  );

  /**
   * Retrieve staff (scoped to either organisation or tenant roles)
   */

  getStaff = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.REQUEST_ALL),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getPageination(selectors.MEMBERS)),
        this.store.select(selectors.getFilter(selectors.MEMBERS)),
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(getCurrentOrganisationTenantId)
      ),
      switchMap(
        ([
          action,
          organisationId,
          pagination = {},
          filter,
          selectedTenantId,
          organisationTenantId,
        ]) => {
          const forModel = filter.modelName;
          const skipPagination = action?.payload?.skipPagination ?? false;
          return this.membersService
            .listMembers(
              {
                search: filter?.search,
                order: filter?.order,
                orderBy: filter?.orderBy,
                limit: skipPagination ? undefined : pagination.pageSize,
                skip: skipPagination ? undefined : pagination.pageSize * pagination.pageIndex,
                enabled: filter?.enabled,
                tenantId:
                  forModel === 'Tenant' || !organisationId
                    ? selectedTenantId ?? organisationTenantId
                    : undefined,
                organisationId:
                  !forModel || forModel === 'Organisation' ? organisationId : undefined,
                roles: filter?.roles ?? undefined,
              },
              'response'
            )
            .pipe(
              switchMap((response) => [
                Members.ResponseSuccessAllAction(response.body),
                Members.ResponseSuccessCountAction(
                  parseInt(response?.headers?.get('x-total-count') ?? '0')
                ),
              ]),
              catchError((err) => observableOf(Members.ResponseFailAllAction(err)))
            );
        }
      )
    )
  );

  searchAllMembers = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.REQUEST_SEARCH_ALL_MEMBERS_ADMIN),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(
        ([action, auth]) =>
          !!(
            auth.roles &&
            auth.roles.find(
              (value) =>
                value === ADMIN_ROLE ||
                value === CAREAPP_SUPPORT_1 ||
                value === CAREAPP_SUPPORT_2 ||
                value === CAREAPP_MANAGER
            )
          )
      ),
      switchMap(([action, auth]) => {
        return this.membersService
          .listMembers({ search: action.payload, order: undefined, orderBy: undefined, limit: 30 })
          .pipe(
            map((result) => Members.ResponseSuccessSearchAction(result)),
            catchError((err) => observableOf(Members.ResponseFailSearchAction(err)))
          );
      })
    )
  );

  searchStaff = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.REQUEST_SEARCH),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, organisationId]) => {
        return this.membersService
          .listMembers({
            search: action.payload?.search,
            order: undefined,
            orderBy: undefined,
            limit: undefined,
            skip: undefined,
            enabled: action.payload?.enabled,
            tenantId: undefined,
            organisationId,
          })
          .pipe(
            map((result) => Members.ResponseSuccessSearchAction(result)),
            catchError((err) => observableOf(Members.ResponseFailSearchAction(err)))
          );
      })
    )
  );

  staffById = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.REQUEST_BY_ID),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.getEntities(selectors.MEMBERS)),
        this.store.select(getSelectedCustomerOrganisationId)
      ),
      // Don't request staff we already have in the store
      // + An organisation ID or Tenant ID is required for this request.
      filter(
        ([action, organisationId, tenantId, existingStaff, selectedCustomerOrganisationId]) => {
          return (
            (action.payload.forceRefresh || !existingStaff[action.payload.id]) &&
            (organisationId || selectedCustomerOrganisationId || tenantId)
          );
        }
      ),
      mergeMap(
        ([action, organisationId, tenantId, existingStaff, selectedCustomerOrganisationId]) => {
          return this.membersService
            .getMember({
              id: action.payload.id,
              organisationId: organisationId ?? selectedCustomerOrganisationId,
              tenantId,
            })
            .pipe(
              map((payload) => {
                return Members.ResponseSuccessByIdAction(payload);
              }),
              catchError((err) => {
                return observableOf(Members.ResponseFailByIdAction(err));
              })
            );
        }
      )
    )
  );

  tenantMemberById = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RequestTenantMemberById),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(getCurrentOrganisationTenantId),
        this.store.select(selectors.getEntities(selectors.MEMBERS))
      ),
      // Don't request staff we already have in the store
      filter(
        ([action, tenantId, organisationId, existingStaff]) => !existingStaff[action.memberId]
      ),
      mergeMap(([action, selectedTenantId, organisationTenantId, existingStaff]) => {
        return this.membersService
          .getMember({
            id: action.memberId,
            organisationId: null, // Organisation
            tenantId: action.tenantId ?? selectedTenantId ?? organisationTenantId,
          })
          .pipe(
            map((payload) => {
              return Members.ResponseSuccessByIdAction(payload);
            }),
            catchError((err) => {
              return observableOf(Members.ResponseFailByIdAction(err));
            })
          );
      })
    )
  );

  getPermissions = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.REQUEST_PERMISSIONS_ADMIN),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(
        ([action, auth]) =>
          !!(
            auth.roles &&
            auth.roles.find(
              (value) =>
                value === ADMIN_ROLE || value === CAREAPP_SUPPORT_1 || value === CAREAPP_MANAGER
            )
          )
      ),
      switchMap(([action, auth]) => {
        return this.membersService.listMemberPermissions({ id: action.payload.id }).pipe(
          map((result) =>
            Members.ResponseSuccessPermissions({ id: action.payload.id, permissions: result })
          ),
          catchError((err) => observableOf(Members.ResponseFailPermissions(err)))
        );
      })
    )
  );

  fetchOwnersForMemo = createEffect(() =>
    this.actions$.pipe(
      ofType(Memos.RESPONSE_SUCCESS_BY_ID),
      map((action) => Members.RequestByIdAction({ id: action.payload.owner }))
    )
  );

  getIdentity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RequestMemberIdentity),
      switchMap((action) =>
        this.accountService.getIdentityForMember({ id: action.memberId }).pipe(
          map((response) =>
            Members.ResponseSuccessMemberIdentity({ memberId: action.memberId, identity: response })
          ),
          catchError((err) =>
            observableOf(
              Members.ResponseFailMemberIdentity({ memberId: action.memberId, error: err })
            )
          )
        )
      )
    )
  );
  getSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RequestMemberSessions),
      switchMap((action) =>
        this.accountService.getSessionsForMember({ id: action.memberId }).pipe(
          map((response) =>
            Members.ResponseSuccessMemberSessions({ memberId: action.memberId, sessions: response })
          ),
          catchError((err) =>
            observableOf(
              Members.ResponseFailMemberSessions({ memberId: action.memberId, error: err })
            )
          )
        )
      )
    )
  );

  updateOrganisationRole = createEffect(() =>
    this.actions$.pipe(
      ofType<CAAction>(Members.REQUEST_UPDATE_ROLE),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      mergeMap(([action, selectedOrganisationId]) => {
        const typedAction = (action as CAAction).payload as {
          id: string;
          role: PermissionUpdateDto;
          organisationId: string;
        };
        const organisationId = selectedOrganisationId ?? typedAction.organisationId;

        return this.permissionService
          .setOrganisationPermission({
            organisationId,
            memberId: typedAction.id,
            permissionUpdateDto: typedAction.role,
          })
          .pipe(
            map((permission) => Members.ResponseSuccessUpdateRole(permission)),
            catchError((err) => observableOf(Members.ResponseFailUpdateRole(err)))
          );
      })
    )
  );

  updatePermission = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RequestUpdatePermission),
      switchMap((action) => {
        return this.permissionService
          .updatePermission({ id: action.permissionId, permissionUpdateDto: action.request })
          .pipe(
            map((permission) => Members.ResponseSuccessUpdatePermission({ permission })),
            catchError((error) =>
              observableOf(
                Members.ResponseFailUpdatePermission({ permissionId: action.permissionId, error })
              )
            )
          );
      })
    )
  );

  convertToTenantAdmin = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RequestConvertToTenantAdmin),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(
        ([action, auth]) =>
          !!(
            auth.roles &&
            auth.roles.find(
              (value) =>
                value === ADMIN_ROLE || value === CAREAPP_SUPPORT_1 || value === CAREAPP_MANAGER
            )
          )
      ),
      switchMap(([action, auth]) => {
        return this.permissionService
          .convertToTenantAdmin({
            convertToTenantAdminRequestDto: {
              memberId: action.memberId,
              tenantId: action.tenantId,
            },
          })
          .pipe(
            map((result) => Members.ResponseSuccessConvertToTenantAdmin({ permission: result })),
            catchError((err) =>
              observableOf(
                Members.ResponseFailConvertToTenantAdmin({
                  error: err,
                  memberId: action.memberId,
                  tenantId: action.tenantId,
                })
              )
            )
          );
      })
    )
  );

  deleteCredential$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Members.RequestDeleteCredential),
      switchMap((action) =>
        this.accountService
          .deleteCredential({ id: action.memberId, type: action.credentialType })
          .pipe(
            map((response) =>
              Members.SuccessDeleteCredential({ identity: response, memberId: action.memberId })
            ),
            catchError((error) => observableOf(Members.ResponseFailDeleteCredential({ error })))
          )
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private membersService: MembersService,
    private permissionService: PermissionsService,
    private accountService: AccountService
  ) {}
}
